/**
 * The browser's connection to the relay.
 *
 * Reconnects with backoff and resumes from the last segment seq it saw, which
 * is what makes a viewer that drops and comes back catch up with no gaps. Also
 * batches latency samples back to the relay so every run produces the
 * phone-to-viewer numbers the test approach asks for.
 */
import {
  decodeEvent,
  encodeEvent,
  makeEvent,
  type Envelope,
  type EventType,
  type Payloads,
  type Role,
} from '@lt/protocol';

export type ConnectionState = 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface RelayClientOptions {
  wsUrl: string;
  httpBase: string;
  token: string;
  role: Exclude<Role, 'relay'>;
  sessionId: string;
  onEvent: (event: Envelope) => void;
  onState: (state: ConnectionState) => void;
  /** Supplies the resume point at connect time, so it is never stale. */
  lastSegmentSeq: () => number | null;
}

const BACKOFF_MS = [250, 500, 1_000, 2_000, 5_000, 10_000];
/** Latency samples are batched rather than posted one at a time. */
const METRICS_FLUSH_MS = 5_000;

export class RelayClient {
  #socket: WebSocket | null = null;
  #attempt = 0;
  #closedByUs = false;
  #clientId = '';
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  #metricsTimer: ReturnType<typeof setInterval> | null = null;
  #samples: Array<{ kind: 'touch' | 'scroll' | 'audio_to_text' | 'label_settle' | 'catchup'; ms: number }> = [];
  readonly #options: RelayClientOptions;

  constructor(options: RelayClientOptions) {
    this.#options = options;
  }

  get clientId(): string {
    return this.#clientId;
  }

  connect(): void {
    this.#closedByUs = false;
    this.#open();
    if (!this.#metricsTimer) {
      this.#metricsTimer = setInterval(() => void this.#flushMetrics(), METRICS_FLUSH_MS);
    }
  }

  #open(): void {
    const { wsUrl, token, role, sessionId, onState, onEvent, lastSegmentSeq } = this.#options;
    onState(this.#attempt === 0 ? 'connecting' : 'reconnecting');

    const socket = new WebSocket(wsUrl);
    this.#socket = socket;

    socket.addEventListener('open', () => {
      this.#attempt = 0;
      socket.send(
        encodeEvent(
          makeEvent('hello', sessionId, { role, clientId: this.#clientId || 'browser' }, {
            token,
            role,
            clientId: this.#clientId || 'browser',
            // Read at connect time, so a reconnect resumes from what we now
            // hold rather than from whatever was true when the client was made.
            lastSegmentSeq: lastSegmentSeq(),
          }) as Envelope,
        ),
      );
    });

    socket.addEventListener('message', (message: MessageEvent<string>) => {
      if (typeof message.data !== 'string') return; // browsers never receive audio
      let event: Envelope;
      try {
        event = decodeEvent(message.data);
      } catch {
        return;
      }
      if (event.type === 'welcome') {
        this.#clientId = (event.payload as Payloads['welcome']).clientId;
        onState('open');
      }
      onEvent(event);
    });

    socket.addEventListener('close', (closeEvent: CloseEvent) => {
      this.#socket = null;
      if (this.#closedByUs) {
        onState('closed');
        return;
      }
      // 4401 means the token was rejected. Retrying cannot fix that, and a
      // reconnect loop would hammer the relay.
      if (closeEvent.code === 4401) {
        onState('closed');
        return;
      }
      this.#scheduleReconnect();
    });

    socket.addEventListener('error', () => {
      // `close` always follows, which is where the retry is scheduled.
    });
  }

  #scheduleReconnect(): void {
    const delay = BACKOFF_MS[Math.min(this.#attempt, BACKOFF_MS.length - 1)]!;
    this.#attempt += 1;
    this.#options.onState('reconnecting');
    this.#reconnectTimer = setTimeout(() => {
      this.#reconnectTimer = null;
      if (!this.#closedByUs) this.#open();
    }, delay);
  }

  send<K extends EventType>(type: K, payload: Payloads[K]): void {
    const socket = this.#socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    socket.send(
      encodeEvent(
        makeEvent(type, this.#options.sessionId, { role: this.#options.role, clientId: this.#clientId }, payload) as Envelope,
      ),
    );
  }

  /** Records a one-way delay for the latency log. Batched; never blocks a frame. */
  recordLatency(kind: 'touch' | 'scroll' | 'catchup', ms: number): void {
    if (!Number.isFinite(ms) || ms < 0 || ms > 60_000) return;
    this.#samples.push({ kind, ms });
    if (this.#samples.length > 200) this.#samples.splice(0, this.#samples.length - 200);
  }

  async #flushMetrics(): Promise<void> {
    if (this.#samples.length === 0) return;
    const samples = this.#samples.splice(0);
    try {
      await fetch(`${this.#options.httpBase}/api/metrics`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${this.#options.token}` },
        body: JSON.stringify({ samples }),
        keepalive: true,
      });
    } catch {
      // Metrics are best-effort; losing a batch must not disturb the meeting.
    }
  }

  close(): void {
    this.#closedByUs = true;
    if (this.#reconnectTimer) clearTimeout(this.#reconnectTimer);
    if (this.#metricsTimer) clearInterval(this.#metricsTimer);
    this.#reconnectTimer = null;
    this.#metricsTimer = null;
    void this.#flushMetrics();
    this.#socket?.close(1000, 'leaving');
    this.#socket = null;
  }
}
